





















































































import theme from '@zk/theme/lib/variables.scss';
import { defineComponent, PropType, computed, ref, toRef, watch } from '@vue/composition-api';

import draggable from 'vuedraggable';
import TheFieldCard from './TheFieldCard.vue';

import { LayoutFieldItem } from '../model/layout';
import { FIXED_FIELDS, FIXED_SORT_FIELDS } from '../common/config';
import { useFieldLayout } from '../store';
import { AREA_CODE_MAP } from '../store/layouts';
import { useInstance } from 'common-module/src/util/useInstance';

import { useResizeListener } from '../utils/useResize';
import { containerByDistance } from '../utils/index';

export default defineComponent({
  name: 'TheLayoutArea',
  components: {
    TheFieldCard,
    draggable
  },
  props: {
    fields: {
      type: Array as PropType<LayoutFieldItem[]>,
      default: () => []
    },
    fieldAreaClass: {
      type: String,
      default: ''
    },
    used: {
      type: Boolean,
      default: false
    },
    areaCode: {
      type: String,
      default: null
    },
    dragGroupSetting: {
      type: Object,
      default: () => ({
        name: 'layout',
        put: true,
        pull: true
      })
    },
    isHighlight: {
      type: Boolean,
      default: false
    },
    distanceBottom: {
      type: Number,
      default: 0
    },
    moveUpdate: {
      type: Function as PropType<(list: LayoutFieldItem[]) => void>,
      default: null
    }
  },
  setup(props, { emit }) {
    const store = useFieldLayout();
    const instance = useInstance();

    const fixedFields = computed<LayoutFieldItem[]>({
      get: () => props.fields?.filter(f => FIXED_FIELDS.includes(f.fieldCode)) || [],
      set: () => { }
    });
    const dragFields = computed<LayoutFieldItem[]>({
      get: () => props.fields?.filter(f => !FIXED_FIELDS.includes(f.fieldCode)) || [],
      set: (val) => {
        const fields = [...fixedFields.value, ...val];
        if (typeof props.moveUpdate === 'function') {
          props.moveUpdate(fields);
          return;
        }
        store.updateLayoutField(fields, props.areaCode);
      }
    });

    const showEmpty = computed(() => !props.fields?.length);
    const loading = computed(() => props.areaCode === 'unused' ? store.fieldsLoading : store.layoutLoading);

    // 是否为拖拽内的固定字段
    const isFixedField = (fieldCode: string) => FIXED_SORT_FIELDS.includes(fieldCode);

    // 区域拖拽状态提示
    const dragAreaStatusTips = computed(() => {
      return store.dragFromCode === props.areaCode
        ? 'Relocate'
        : (props.areaCode === 'unused' ? 'Remove' : 'Insert');
    });

    // 拖拽事件处理
    const onDragStart = (event: any) => {
      const fromAreaCode = event.from?.getAttribute('data-area-code');
      const fieldCode = event.item?.getAttribute('data-field-code');
      const fieldInfo = event.item?._underlying_vm_;

      store.updateDragStatus(true, fieldCode, fieldInfo, fromAreaCode);
      emit('drag-start', event);
      document.documentElement.classList.add('draggable-cursor-grabbing');
    };
    const onDragEnd = (event: any) => {
      store.updateDragStatus(false);
      emit('drag-end', event);
      document.documentElement.classList.remove('draggable-cursor-grabbing');
    };

    // 拖拽区域高度
    const areaRef = ref(null);
    const draggableRef = ref(null);
    const dropAreaHeight = ref<string>('auto');
    const isDropScroll = ref<boolean>(false); // 该拖拽区域是否滚动

    const setAreaBoxHeight = async () => {
      const distanceBottom = props.distanceBottom + (props.isHighlight ? 50 : 0);
      const draggableElement = instance.$refs.draggableRef?.$el;
      if (!draggableElement) {
        return;
      }
      const containerHeight = containerByDistance(
        draggableElement,
        document.getElementsByTagName('body')[0],
        distanceBottom
      );
      dropAreaHeight.value = containerHeight ? `${containerHeight}px` : 'auto';
    };

    /**
     * 更新拖拽区域是否滚动
     * setAreaBoxHeight 和 updateScrollStatus 都是基于draggableRef进行计算，
     * 这里拆分函数调用是因为dropAreaHeight更新后，视图会重新进行渲染，导致draggableRef更新，所以需要等待视图渲染完成后再进行计算
    */
    const updateScrollStatus = async () => {
      const draggableElement = instance.$refs.draggableRef?.$el;
      if (!draggableElement) {
        return;
      }
      await instance.$nextTick();
      isDropScroll.value = draggableElement.scrollHeight > draggableElement.clientHeight;
    };

    watch(
      () => [props.isHighlight, props.distanceBottom, props.fields?.length],
      async () => {
        setTimeout(() => {
          setAreaBoxHeight();
          updateScrollStatus();
        }, 0);
      }
    );

    useResizeListener(setAreaBoxHeight);

    // 固定字段显隐控制
    const onFixedVisible = (field: LayoutFieldItem) => {
      const showed = Number(field.showed) === 1 ? 0 : 1;
      store.updateFieldInLayout(
        {
          ...field,
          showed,
          requiredFlag: showed ? field.requiredFlag : 0
        },
        toRef(store, AREA_CODE_MAP[props.areaCode])
      );
    };

    // 必填设置
    const onRequiredChange = (
      check: boolean,
      field: LayoutFieldItem
    ) => {
      store.updateFieldInLayout(
        { ...field, requiredFlag: check ? 1 : 0 },
        toRef(store, AREA_CODE_MAP[props.areaCode])
      );
    };

    // 点击移除字段
    const onRemoveField = (field: LayoutFieldItem) => {
      store.deletedFieldsInArea(field, toRef(store, AREA_CODE_MAP[props.areaCode]));
      store.fieldInsertLayout({ ...field, requiredFlag: 0 }, toRef(store, AREA_CODE_MAP.unused));
    };

    return {
      fixedFields,
      dragFields,
      dropAreaHeight,
      areaRef,
      draggableRef,
      dragAreaStatusTips,
      isDropScroll,
      showEmpty,
      loading,
      onDragStart,
      onDragEnd,
      onFixedVisible,
      onRequiredChange,
      onRemoveField,
      isFixedField,
      doUpdateLayout: () => {
        setTimeout(setAreaBoxHeight, 0);
      }
    };
  },
  data() {
    return {
      bemNS: theme.bemNS
    };
  }
});
